import type { MiddlewareState } from '@floating-ui/dom';
import { autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react-dom';
import { type Ref, useMemo } from 'react';

/** Gap when the menu is **above** the trigger (`top-*`). */
const OFFSET_MAIN_AXIS_ABOVE_PX = 24;
/** Gap when the menu is **below** the trigger (`bottom-*`). */
const OFFSET_MAIN_AXIS_BELOW_PX = -8;
const SHIFT_PADDING_PX = 8;

/**
 * Same idea as core-ui `Dropdown`: `offset` + `flip` (overflow) + `shift` + `autoUpdate`.
 * Menu height is fixed via CSS `max-height` on the panel — no `size` middleware so
 * stylesheet caps are not overridden by inline styles.
 */
export type UseFloatingSelectMenuOptions = {
  isOpen: boolean;
  /** `true` = welcome (prefer below); `false` = composer at bottom (prefer above). */
  preferredBelow: boolean;
  /**
   * Optional main-axis gap (px) for `top-*` / `bottom-*` placements.
   * Defaults match Agent selector; Instance passes symmetric gaps (see `spacing.r8`).
   */
  mainAxisAbovePx?: number;
  mainAxisBelowPx?: number;
};

export function useFloatingSelectMenu({
  isOpen,
  preferredBelow,
  mainAxisAbovePx,
  mainAxisBelowPx,
}: UseFloatingSelectMenuOptions) {
  const placement = preferredBelow ? 'bottom-start' : 'top-start';

  const middleware = useMemo(
    () => [
      offset((state: MiddlewareState) => {
        const p = state.placement;
        const below = mainAxisBelowPx ?? OFFSET_MAIN_AXIS_BELOW_PX;
        const above = mainAxisAbovePx ?? OFFSET_MAIN_AXIS_ABOVE_PX;
        const mainAxis = p.startsWith('bottom') ? below : p.startsWith('top') ? above : above;
        return { mainAxis };
      }),
      flip({
        fallbackPlacements: preferredBelow ? ['top-start', 'bottom-start'] : ['bottom-start', 'top-start'],
      }),
      shift({ padding: SHIFT_PADDING_PX }),
    ],
    [preferredBelow, mainAxisAbovePx, mainAxisBelowPx],
  );

  return useFloating({
    open: isOpen,
    placement,
    strategy: 'fixed',
    middleware,
    whileElementsMounted: isOpen ? autoUpdate : undefined,
  });
}

export function mergeRefs<T>(...refs: (Ref<T> | undefined)[]): (node: T | null) => void {
  return (node) => {
    for (const ref of refs) {
      if (ref == null) continue;
      if (typeof ref === 'function') ref(node);
      else ref.current = node;
    }
  };
}
